import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

/** Monday-based week (ISO). */
export const WEEK_STARTS_ON = 1;

export function monthGridBounds(visibleMonth) {
  const start = startOfMonth(visibleMonth);
  const end = endOfMonth(visibleMonth);
  const gridStart = startOfWeek(start, { weekStartsOn: WEEK_STARTS_ON });
  const gridEnd = endOfWeek(end, { weekStartsOn: WEEK_STARTS_ON });
  return { gridStart, gridEnd, days: eachDayOfInterval({ start: gridStart, end: gridEnd }) };
}

export function formatDayKey(d) {
  return format(d, 'yyyy-MM-dd');
}

export const DEFAULT_DAY_HOURS = [8, 12, 16, 20];

/**
 * Pick the default hour for the nth planned session on a day.
 * - Today: next full hour from now (+ n to stagger multiple drops).
 * - Other days: cycle through 8 → 12 → 16 → 20.
 */
export function defaultHourForNthPlanned(n, dayKey) {
  if (dayKey && dayKey === formatDayKey(new Date())) {
    const now = new Date();
    const baseHour =
      now.getMinutes() > 0 || now.getSeconds() > 0
        ? now.getHours() + 1
        : now.getHours();
    return Math.min(baseHour + n, 23);
  }
  return DEFAULT_DAY_HOURS[n % DEFAULT_DAY_HOURS.length];
}

export function localOccurrenceAt(dayKey, hour, minute = 0) {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d, hour, minute, 0, 0);
}

export function shiftDateKeepTime(prevIso, newDayKey) {
  const prev = new Date(prevIso);
  if (Number.isNaN(prev.getTime())) return localOccurrenceAt(newDayKey, 8, 0);
  const [y, m, d] = newDayKey.split('-').map(Number);
  return new Date(y, m - 1, d, prev.getHours(), prev.getMinutes(), prev.getSeconds(), 0);
}

export function parseItemMetadata(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function normalizePlannerItem(row) {
  return {
    ...row,
    metadata: parseItemMetadata(row.metadata),
  };
}

export function getPlannerSplits(item) {
  const ure = item.user_running_exercise;
  const hasUre = !!(item.user_running_exercise_id && ure);
  const ureSplits = hasUre && Array.isArray(ure.preview_splits) ? ure.preview_splits : [];
  const ts = item.training_session;
  const libSplits = ts?.preview_splits;
  return hasUre ? (ureSplits.length > 0 ? ureSplits : libSplits || []) : libSplits || [];
}

export function getPlannerTitle(item) {
  const ure = item.user_running_exercise;
  const ts = item.training_session;
  const hasUre = !!(item.user_running_exercise_id && ure);
  if (hasUre) return ure.title || item.title || ts?.name || '';
  return item.title || ure?.title || ts?.name || '';
}

/** Local `strava_activities.id` stored on planned_session `metadata` when linked to a logged activity. */
export const LINKED_STRAVA_ACTIVITY_META_KEY = 'linked_strava_activity_id';

export function hasLinkedStravaActivity(item) {
  const v = item?.metadata?.[LINKED_STRAVA_ACTIVITY_META_KEY];
  return v != null && Number(v) > 0;
}

/**
 * Training planner month cell: hide planned chips that are duplicates of logged Strava
 * activities on the same day, while keeping independently added planned sessions visible.
 *
 * A planned item is hidden when:
 * - Its `linked_strava_activity_id` metadata points to a Strava activity on this day, OR
 * - Its `user_running_exercise_id` matches the URE linked from a Strava activity on this day
 *
 * @param {object[]|null|undefined} plannedItems
 * @param {{ stravaActivities?: { id?: number, userRunningExerciseId?: number|null }[] }|null|undefined} activityDaySummary — `/planner-activity-summary` byDay[ymd]
 */
export function filterPlannerDayPlannedForDisplay(plannedItems, activityDaySummary) {
  const items = plannedItems || [];
  const activities = activityDaySummary?.stravaActivities;
  if (!Array.isArray(activities) || activities.length === 0) return items;

  const linkedUreIds = new Set();
  const stravaActivityIds = new Set();
  for (const sa of activities) {
    const ureId = Number(sa?.userRunningExerciseId);
    if (Number.isFinite(ureId) && ureId > 0) linkedUreIds.add(ureId);
    const aid = Number(sa?.id);
    if (Number.isFinite(aid) && aid > 0) stravaActivityIds.add(aid);
  }
  if (linkedUreIds.size === 0 && stravaActivityIds.size === 0) return items;

  return items.filter((item) => {
    const metaLink = Number(item?.metadata?.[LINKED_STRAVA_ACTIVITY_META_KEY]);
    if (Number.isFinite(metaLink) && metaLink > 0 && stravaActivityIds.has(metaLink)) return false;

    const ureId = Number(item.user_running_exercise_id);
    if (Number.isFinite(ureId) && ureId > 0 && linkedUreIds.has(ureId)) return false;

    return true;
  });
}

/**
 * For day modal: merge matching `activity` + `planned_session` (metadata.linked_strava_activity_id)
 * into one entry so the UI shows a single card.
 */
export function buildTrainingDayDisplayEntries(sortedItems) {
  const items = sortedItems || [];
  const out = [];
  for (const it of items) {
    if (it.type_key === 'activity') {
      const aid = Number(it.strava_activity_id);
      const hasLinkedPlanned = items.some(
        (p) =>
          p.type_key === 'planned_session' &&
          Number(p.metadata?.[LINKED_STRAVA_ACTIVITY_META_KEY]) === aid
      );
      if (hasLinkedPlanned) continue;
    }
    if (it.type_key === 'planned_session') {
      const aid = Number(it.metadata?.[LINKED_STRAVA_ACTIVITY_META_KEY]);
      if (aid > 0) {
        const act = items.find((x) => x.type_key === 'activity' && Number(x.strava_activity_id) === aid);
        if (act) {
          out.push({ kind: 'combined', planned: it, activity: act });
          continue;
        }
      }
    }
    out.push({ kind: 'single', item: it });
  }
  return out;
}

/** Sum distance-based splits to total km; null if no distance data. */
export function sumSessionDistanceKmFromSplits(splits) {
  if (!Array.isArray(splits) || splits.length === 0) return null;
  let m = 0;
  for (const s of splits) {
    const dm = Number(s.distance_m) || 0;
    if (dm > 0) m += dm;
  }
  return m > 0 ? m / 1000 : null;
}

/** Tailwind text color classes for Strava suffer score (relative effort). */
export function sufferScoreTextClass(score) {
  if (score == null || !Number.isFinite(Number(score))) return 'text-muted-foreground';
  const n = Number(score);
  if (n <= 30) return 'text-emerald-600 dark:text-emerald-400';
  if (n <= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-destructive';
}

export { addMonths, startOfMonth, format };
